import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/**
 * Bulk priority change (W4, B2-liste, QW-d4): replaces the browser's
 * `prompt()`, which could not be styled, translated or checked. The value is
 * an integer from 0 to 10 (the filter reads 5+ as high, 1–4 as medium, 0 as
 * low).
 */
export const PRIORITY_MIN = 0;
export const PRIORITY_MAX = 10;

export function parsePriority(raw: string): number | null {
  if (!/^\s*\d+\s*$/.test(raw)) return null;
  const n = Number(raw.trim());
  return n >= PRIORITY_MIN && n <= PRIORITY_MAX ? n : null;
}

export function PriorityDialog({ open, count, onOpenChange, onSubmit }: {
  open: boolean;
  count: number;
  onOpenChange: (open: boolean) => void;
  onSubmit: (priority: number) => void;
}) {
  const { t } = useTranslation(['dialer', 'common']);
  const [value, setValue] = useState('5');
  const parsed = parsePriority(value);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (parsed === null) return;
    onSubmit(parsed);
  };
  return (
    <Dialog open={open} onOpenChange={(o: boolean) => { if (!o) setValue('5'); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-sm">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{t('dialer:contacts.priorityDialog.title', { count })}</DialogTitle>
            <DialogDescription>{t('dialer:contacts.priorityDialog.description')}</DialogDescription>
          </DialogHeader>
          <label className="block space-y-1.5 text-sm">
            <span>{t('dialer:contacts.priorityDialog.label')}</span>
            <Input
              type="number"
              inputMode="numeric"
              min={PRIORITY_MIN}
              max={PRIORITY_MAX}
              step={1}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-invalid={parsed === null}
              aria-describedby="priority-dialog-error"
              autoFocus
            />
          </label>
          {parsed === null && (
            <p id="priority-dialog-error" className="text-[12px]" style={{ color: 'var(--vf-red)' }}>
              {t('dialer:contacts.priorityDialog.invalid', { min: PRIORITY_MIN, max: PRIORITY_MAX })}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common:actions.cancel')}</Button>
            <Button type="submit" disabled={parsed === null}>{t('dialer:contacts.priorityDialog.submit')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
